import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Stack,
  Typography,
  Alert,
} from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { ptBR } from 'date-fns/locale';
import {
  startOfMonth,
  endOfMonth,
  subMonths,
  subDays,
  differenceInDays,
} from 'date-fns';

interface DateRangePickerProps {
  startDate: Date | null;
  endDate: Date | null;
  onDateChange: (start: Date, end: Date) => void;
  loading?: boolean;
  /** Slot para ações contextuais renderizadas ao lado dos campos de data (ex.: reprocessar período). */
  actions?: React.ReactNode;
  /**
   * Número máximo de dias permitido no intervalo selecionado.
   * Default: 180. Consumidores existentes que não passam a prop continuam com o comportamento anterior.
   */
  maxIntervalDays?: number;
}

const DateRangePicker: React.FC<DateRangePickerProps> = ({
  startDate,
  endDate,
  onDateChange,
  loading = false,
  actions,
  maxIntervalDays = 180,
}) => {
  const [localStart, setLocalStart] = useState<Date | null>(startDate);
  const [localEnd, setLocalEnd] = useState<Date | null>(endDate);

  // Fallback: aplica "últimos 30 dias" ao montar se não houver valor
  useEffect(() => {
    if (startDate === null && endDate === null) {
      applyLast30Days();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Sincroniza estado local quando props mudam externamente
  useEffect(() => {
    setLocalStart(startDate);
    setLocalEnd(endDate);
  }, [startDate, endDate]);

  const applyRange = (start: Date, end: Date) => {
    setLocalStart(start);
    setLocalEnd(end);
    onDateChange(start, end);
  };

  const applyCurrentMonth = () => {
    const now = new Date();
    applyRange(startOfMonth(now), now);
  };

  const applyPreviousMonth = () => {
    const lastMonth = subMonths(new Date(), 1);
    applyRange(startOfMonth(lastMonth), endOfMonth(lastMonth));
  };

  const applyLast7Days = () => {
    const today = new Date();
    applyRange(subDays(today, 6), today);
  };

  const applyLast30Days = () => {
    const today = new Date();
    applyRange(subDays(today, 29), today);
  };

  const hasInvalidRange =
    localStart !== null &&
    localEnd !== null &&
    localEnd < localStart;

  const intervalDays =
    localStart !== null && localEnd !== null
      ? differenceInDays(localEnd, localStart)
      : 0;

  const isIntervalTooLong = intervalDays > maxIntervalDays;

  const handleStartChange = (date: Date | null) => {
    setLocalStart(date);
    if (date && localEnd && date <= localEnd) {
      onDateChange(date, localEnd);
    }
  };

  const handleEndChange = (date: Date | null) => {
    setLocalEnd(date);
    if (localStart && date && localStart <= date) {
      onDateChange(localStart, date);
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ptBR}>
      <Box>
        <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mb: 1.5 }}>
          <Button
            variant="outlined"
            size="small"
            disabled={loading}
            onClick={applyCurrentMonth}
          >
            Mês atual
          </Button>
          <Button
            variant="outlined"
            size="small"
            disabled={loading}
            onClick={applyPreviousMonth}
          >
            Mês anterior
          </Button>
          <Button
            variant="outlined"
            size="small"
            disabled={loading}
            onClick={applyLast7Days}
          >
            Últimos 7 dias
          </Button>
          <Button
            variant="contained"
            size="small"
            disabled={loading}
            onClick={applyLast30Days}
            sx={{ fontWeight: 600 }}
          >
            Últimos 30 dias
          </Button>
        </Stack>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'stretch', sm: 'center' }}>
          <DatePicker
            label="Data inicial"
            value={localStart}
            onChange={handleStartChange}
            disabled={loading}
            slotProps={{
              textField: {
                size: 'small',
                error: hasInvalidRange,
                helperText: hasInvalidRange ? 'Data inicial deve ser anterior à data final' : undefined,
              },
            }}
          />
          <DatePicker
            label="Data final"
            value={localEnd}
            onChange={handleEndChange}
            disabled={loading}
            slotProps={{
              textField: {
                size: 'small',
                error: hasInvalidRange,
                helperText: hasInvalidRange ? 'Data final deve ser posterior à data inicial' : undefined,
              },
            }}
          />
          {actions}
        </Stack>
        {isIntervalTooLong && !hasInvalidRange && (
          <Alert severity="warning" sx={{ mt: 1 }}>
            <Typography variant="body2">
              Intervalo máximo permitido: {maxIntervalDays} dias. Selecione um período menor.
            </Typography>
          </Alert>
        )}
      </Box>
    </LocalizationProvider>
  );
};

export default DateRangePicker;
